/**
 * @fileoverview Input/output schemas for weave-snapshotter-v2.
 *
 * This agent synthesizes continuity data from Stage 1 outputs (plot + character
 * snapshots) against structural entities (storylines, arcs, threads) to produce
 * storyline activity, beat touches, and arc progression.
 *
 * Plot thread lifecycle management has been moved to the dedicated
 * plot-thread-snapshotter-v1 agent. Threads are passed as read-only context
 * for cross-referencing with storylines and arcs.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Simplified storyline representation for the weave agent input.
 */
const WeaveStorylineSchema = z.object({
  id: z.string(),
  name: z.string(),
  summary: z.string().optional(),
  priority: z.enum(['A', 'B', 'C']),
  beats: z.array(z.object({
    id: z.string(),
    globalPhase: z.string(),
    storylinePhase: z.string(),
    plotEvent: z.string(),
    goalProgress: z.number(),
  })),
  storyQuestions: z.array(z.object({
    id: z.string(),
    question: z.string(),
    status: z.enum(['open', 'answered']),
  })),
});

/**
 * Simplified thread representation for the weave agent input.
 * Read-only context — thread lifecycle is managed by plot-thread-snapshotter-v1.
 */
const WeaveExistingThreadSchema = z.object({
  id: z.string(),
  title: z.string(),
  threadQuestion: z.string(),
  summary: z.string(),
  status: z.enum(['active', 'resolved', 'abandoned']),
});

/**
 * Character arc data for the weave agent input.
 */
const WeaveCharacterArcSchema = z.object({
  characterId: z.string(),
  characterName: z.string(),
  arcName: z.string(),
  arcSummary: z.string(),
  beats: z.array(z.object({
    id: z.string(),
    globalPhase: z.string().describe(
      'Where this beat sits in the overall story timeline.'
    ),
    arcPhase: z.string().describe(
      'The structural function this beat serves for this character\'s internal arc. ' +
      'For characters with offset arcs, arcPhase differs from globalPhase.'
    ),
    structurePhase: z.string().optional().describe(
      'Deprecated. Retained for backward compatibility — prefer globalPhase + arcPhase.'
    ),
    internalState: z.string(),
    behavior: z.string().optional(),
    arcProgress: z.number(),
  })),
});

/**
 * Stage 1 event data piped from the parallel snapshotters.
 */
const MajorPlotEventSchema = z.object({
  id: z.string(),
  description: z.string(),
});

const MajorCharacterEventsSchema = z.object({
  characterName: z.string(),
  events: z.array(z.object({
    id: z.string(),
    description: z.string(),
  })),
});

/**
 * Full input for the weave snapshotter flow.
 */
export const InputSchema = z.object({
  ...BaseFlowInputSchema.shape,
  ...AGENT_SCHEMA_OBJECTS.storyContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.chapterContent.schema.shape,

  // Structural context
  existingStorylines: z.array(WeaveStorylineSchema).describe(
    'All storylines defined in the story planning, with their beats and questions.'
  ),
  existingThreads: z.array(WeaveExistingThreadSchema).optional().describe(
    'All plot threads (read-only context for cross-referencing with storylines/arcs).'
  ),
  characterArcs: z.array(WeaveCharacterArcSchema).optional().describe(
    'Character arcs with their planned beats, for arc-touch detection.'
  ),

  // Stage 1 piped outputs
  majorPlotEvents: z.array(MajorPlotEventSchema).optional().describe(
    'Major plot events extracted by the plot snapshotter (Stage 1).'
  ),
  majorCharacterEvents: z.array(MajorCharacterEventsSchema).optional().describe(
    'Per-character major events extracted by the character snapshotter (Stage 1).'
  ),
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

/**
 * Weave agent output. Matches the WeaveSnapshot contract minus id/chapterId/timestamp
 * which are added by the hook during persistence.
 */
export const OutputSchema = z.object({
  storylineActivity: z.array(z.object({
    storylineId: z.string().describe('ID of the storyline active in this chapter.'),
    tensionLevel: z.enum(['low', 'rising', 'high', 'release']).describe('Tension level for this storyline.'),
    evidence: z.string().describe('Brief evidence from the chapter text.'),
  })).describe('Which storylines are active in this chapter with tension levels.'),

  storylineQuestionUpdates: z.array(z.object({
    storylineId: z.string().describe('ID of the parent storyline.'),
    questionId: z.string().describe('ID of the story question.'),
    status: z.enum(['open', 'answered']).describe('Updated status of the question.'),
    confidence: z.number().describe('Confidence score (0-1).'),
    evidence: z.string().describe('Brief evidence from the chapter text.'),
  })).describe('Story question status changes detected.'),

  storylineBeatTouches: z.array(z.object({
    storylineId: z.string().describe('ID of the parent storyline.'),
    beatId: z.string().describe('ID of the storyline beat.'),
    touchType: z.enum(['foreshadowed', 'advanced', 'fulfilled']).describe('How this beat was touched.'),
    confidence: z.number().describe('Confidence score (0-1).'),
    evidence: z.string().describe('Brief evidence from the chapter text.'),
  })).describe('Storyline beats touched in this chapter.'),

  characterArcTouches: z.array(z.object({
    characterId: z.string().describe('ID of the character.'),
    arcBeatId: z.string().optional().describe('ID of the arc beat, if matched to a specific beat.'),
    structurePhase: z.string().optional().describe('Structure phase of the arc beat touched.'),
    touchType: z.enum(['foreshadowed', 'advanced', 'fulfilled']).describe('How this arc beat was touched.'),
    confidence: z.number().describe('Confidence score (0-1).'),
    evidence: z.string().describe('Brief evidence from the chapter text.'),
  })).describe('Character arc beats touched in this chapter.'),
});

export type Output = z.infer<typeof OutputSchema>;
